import { useNavigate, useParams } from "react-router-dom";
import LiveCallsSection from "./LiveCallsSection";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import VoicePreviewButton from "@/components/ui/voice-preview-button";
import { ArrowLeft, Loader2, Phone, PhoneOff } from "lucide-react";
import { toast } from "sonner";
import React, { useState } from "react";
import { REALTIME_VOICES_OPENAI } from "@/model/interface/AiAgent";

const REALTIME_VOICE_UNSET = "__default__";
import {
  useGetAiAgentQuery,
  useUpdateAiAgentMutation,
  useGetAgentVoicesQuery,
  useGetAgentPhoneNumbersQuery,
  useAssignPhoneNumberToAgentMutation,
  useUnassignPhoneNumberFromAgentMutation,
  usePlaceAgentTestCallMutation,
  useGetOwnPhoneNumbersQuery,
  useGrantConsentByNumberMutation,
} from "@/store/rest_api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const agentSchema = z.object({
  name: z.string().min(1, "Name is required"),
  voiceId: z.string().optional(),
  realtimeVoice: z.string().optional(),
  greeting: z.string().optional(),
  closingMessage: z.string().optional(),
});

type AgentFormValues = z.infer<typeof agentSchema>;

export default function AiAgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: agent, isLoading } = useGetAiAgentQuery(id!);
  const [updateAgent, { isLoading: isSaving }] = useUpdateAiAgentMutation();
  // Voices are filtered server-side to this agent's provider, so the org UI never sees the provider name.
  const { data: voices } = useGetAgentVoicesQuery(id!);
  const [placeTestCall, { isLoading: isPlacingTestCall }] = usePlaceAgentTestCallMutation();

  const { data: assignedNumbers, isLoading: isLoadingNumbers } = useGetAgentPhoneNumbersQuery(id!);
  const { data: allNumbers } = useGetOwnPhoneNumbersQuery();
  const [assignNumber, { isLoading: isAssigning }] = useAssignPhoneNumberToAgentMutation();
  const [unassignNumber] = useUnassignPhoneNumberFromAgentMutation();

  const [grantConsentByNumber, { isLoading: isGrantingConsent }] = useGrantConsentByNumberMutation();

  const [testCallNumber, setTestCallNumber] = useState("");
  const [testCallFromId, setTestCallFromId] = useState("");
  const [testCallDialogOpen, setTestCallDialogOpen] = useState(false);
  const [consentPromptOpen, setConsentPromptOpen] = useState(false);

  const form = useForm<AgentFormValues>({
    resolver: zodResolver(agentSchema),
    values: agent
      ? {
          name: agent.name || "",
          // Pre-fill the editable fields with the resolved value (agent override ?? template default),
          // so org users see and can tweak the admin-configured greeting/tools/outcomes. (The system
          // prompt is no longer editable here — it's managed centrally in the admin prompt library.)
          voiceId: agent.voiceId || "",
          realtimeVoice: (agent.customConfig?.realtimeVoice as string | undefined) || "",
          greeting: agent.effectiveGreeting ?? agent.greeting ?? "",
          closingMessage: agent.effectiveClosingMessage ?? agent.closingMessage ?? "",
        }
      : undefined,
    defaultValues: {
      name: "",
      voiceId: "",
      realtimeVoice: "",
      greeting: "",
      closingMessage: "",
    },
  });

  const isRealtime = agent?.isRealtime ?? false;

  const onSubmit = async (values: AgentFormValues) => {
    try {
      const { realtimeVoice, ...rest } = values;
      const payload: Record<string, any> = {
        ...rest,
        voiceId: rest.voiceId?.trim() || null,
        greeting: rest.greeting?.trim() || null,
        closingMessage: rest.closingMessage?.trim() || null,
      };
      // customConfig is replaced wholesale by the backend, so merge with the existing config to
      // preserve other keys (silence settings, transferNumber, etc.). realtimeVoice only applies
      // to realtime orchestrators.
      payload.customConfig = {
        ...(agent?.customConfig ?? {}),
        ...(isRealtime ? { realtimeVoice: realtimeVoice?.trim() || null } : {}),
      };
      await updateAgent({ id: id!, data: payload }).unwrap();
      toast.success("Agent updated");
    } catch (updateError: any) {
      toast.error(updateError.detail || "Failed to update agent");
    }
  };

  const handlePlaceTestCall = async (promptForConsent = true) => {
    if (!testCallNumber.trim()) {
      toast.error("Enter a phone number");
      return;
    }
    try {
      await placeTestCall({
        agentId: id!,
        toPhoneNumber: testCallNumber.trim(),
        fromPhoneNumberId: testCallFromId || undefined,
      }).unwrap();
      toast.success("Test call placed");
      setTestCallDialogOpen(false);
      setTestCallNumber("");
      setTestCallFromId("");
    } catch (testCallError: any) {
      if (testCallError?.data?.code === "NO_CONSENT" && promptForConsent) {
        setConsentPromptOpen(true);
        return;
      }
      toast.error(testCallError?.data?.detail || "Failed to place test call");
    }
  };

  const handleRecordConsentAndCall = async () => {
    try {
      await grantConsentByNumber({
        phoneNumberE164: testCallNumber.trim(),
        consentType: "CALL",
        source: "MANUAL",
        notes: "Recorded from an AI agent test call",
      }).unwrap();
      setConsentPromptOpen(false);
      toast.success("Call consent recorded");
      await handlePlaceTestCall(false);
    } catch (grantError: any) {
      toast.error(grantError?.data?.detail || "Failed to record consent");
    }
  };

  const handleAssignNumber = async (phoneNumberId: string) => {
    try {
      await assignNumber({ agentId: id!, phoneNumberId }).unwrap();
      toast.success("Phone number assigned");
    } catch (err: any) {
      toast.error(err?.data?.detail || "Failed to assign phone number");
    }
  };

  const handleUnassignNumber = async (phoneNumberId: string) => {
    try {
      await unassignNumber({ agentId: id!, phoneNumberId }).unwrap();
      toast.success("Phone number unassigned");
    } catch (err: any) {
      toast.error(err?.data?.detail || "Failed to unassign phone number");
    }
  };

  if (isLoading || !agent) {
    return (
      <div className="space-y-6 p-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/ai-agents")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Agent Settings</h1>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-12">
            <div className="space-y-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/3" />
              <div className="h-10 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-1/4" />
              <div className="h-20 bg-muted rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedVoiceId = form.watch("voiceId");
  const selectedVoice = voices?.find((voice) => voice.id === selectedVoiceId);
  // Already filtered server-side to the agent's provider (the org UI never learns which provider).
  const providerVoices = voices ?? [];

  const assignedIds = new Set(assignedNumbers?.map((n) => n.id) ?? []);
  const availableToAssign = allNumbers?.filter((n) => !assignedIds.has(n.id) && n.active) ?? [];

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/ai-agents")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">{agent.name}</h1>
          <p className="text-muted-foreground">Manage how this agent sounds and behaves</p>
        </div>
        <Button variant="outline" onClick={() => setTestCallDialogOpen(true)}>
          <Phone className="mr-1 h-4 w-4" />
          Place Test Call
        </Button>
      </div>

      {/* Only this agent's live calls, and only when there are any. */}
      <LiveCallsSection agentId={id} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Agent Configuration</CardTitle>
              <CardDescription>
                Fine-tune this agent. Leave fields empty to keep its standard behavior.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="greeting"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Greeting</FormLabel>
                    <FormControl>
                      <Input placeholder={agent.defaultGreeting || "Hello! How can I help you?"} {...field} />
                    </FormControl>
                    <FormDescription>The first thing the agent says when answering a call</FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="closingMessage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Closing message</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={2}
                        placeholder={
                          agent.defaultClosingMessage ||
                          "Thanks for your time. We'll send a confirmation by email shortly."
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      How the agent should wrap up when the call ends well. Unlike the greeting this isn't read
                      out word-for-word — the agent works it into its goodbye and still confirms whatever you
                      agreed. It's skipped when a call ends badly (not interested, hang-up, voicemail). Leave
                      blank to use the default.
                    </FormDescription>
                  </FormItem>
                )}
              />

              {/* System-prompt editing has moved to the admin-managed prompt library — org users no
                  longer edit prompt text here; a platform admin assigns a versioned prompt. */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isRealtime ? (
                  <FormField
                    control={form.control}
                    name="realtimeVoice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Voice</FormLabel>
                        <Select
                          value={field.value?.trim() || REALTIME_VOICE_UNSET}
                          onValueChange={(v) => field.onChange(v === REALTIME_VOICE_UNSET ? "" : v)}
                        >
                          <FormControl>
                            <SelectTrigger><SelectValue placeholder="Use default" /></SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={REALTIME_VOICE_UNSET}>Default</SelectItem>
                            {REALTIME_VOICES_OPENAI.map((v) => (
                              <SelectItem key={v.value} value={v.value}>
                                {v.label} · {v.description}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>The voice this agent speaks with.</FormDescription>
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="voiceId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Voice</FormLabel>
                        <div className="flex gap-2">
                          <Select key={voices ? "voices-ready" : "voices-loading"} value={field.value || ""} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger className="flex-1 [&_.voice-extra]:hidden">
                                <SelectValue placeholder="Use the standard voice" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent position="popper" sideOffset={4} className="max-h-[300px]">
                              {providerVoices.map((voice) => (
                                <SelectItem key={voice.id} value={voice.id} className="pr-2">
                                  <span className="flex items-center justify-between gap-2 w-full">
                                    <span className="flex flex-col min-w-0">
                                      <span className="truncate">
                                        {voice.name}
                                        {voice.gender ? ` · ${voice.gender}` : ""}
                                        {voice.accent ? ` · ${voice.accent}` : ""}
                                      </span>
                                      {voice.description ? (
                                        <span className="voice-extra text-xs text-muted-foreground truncate">{voice.description}</span>
                                      ) : null}
                                    </span>
                                    <VoicePreviewButton previewUrl={voice.previewUrl} label={voice.name} className="voice-extra h-6 w-6 shrink-0" />
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <VoicePreviewButton previewUrl={selectedVoice?.previewUrl} label={selectedVoice?.name} />
                        </div>
                        <FormDescription>
                          Voice used for this agent during calls. Use the play button to preview a voice before choosing.
                        </FormDescription>
                      </FormItem>
                    )}
                  />
                )}

              </div>
              {/* Prompt, tools, declared outcomes and transfer routing are admin-managed (templates /
                  deployed-agents admin) — org users configure only name, greeting, voice, and numbers. */}
            </CardContent>
          </Card>

          {/* Phone Numbers */}
          <Card>
            <CardHeader>
              <CardTitle>Inbound Phone Numbers</CardTitle>
              <CardDescription>
                Calls to these numbers will be answered by this agent. A number can only be assigned to one agent at a time.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoadingNumbers ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                </div>
              ) : assignedNumbers && assignedNumbers.length > 0 ? (
                <div className="space-y-2">
                  {assignedNumbers.map((num) => (
                    <div key={num.id} className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{num.number}</span>
                        {num.alias && <span className="text-xs text-muted-foreground">({num.alias})</span>}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnassignNumber(num.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <PhoneOff className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No phone numbers assigned yet.</p>
              )}

              {availableToAssign.length > 0 && (
                <div className="flex items-center gap-2 pt-1">
                  <Select value="" onValueChange={(phoneNumberId) => handleAssignNumber(phoneNumberId)}>
                    <SelectTrigger className="w-[280px]">
                      <SelectValue placeholder="Assign a phone number…" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableToAssign.map((num) => (
                        <SelectItem key={num.id} value={num.id}>
                          {num.number}
                          {num.alias ? ` — ${num.alias}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isAssigning && <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
              )}

              {availableToAssign.length === 0 && !isLoadingNumbers && (
                <p className="text-xs text-muted-foreground">
                  All active phone numbers are already assigned or your account has no active numbers.
                </p>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate("/ai-agents")}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </form>
      </Form>

      {testCallDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setTestCallDialogOpen(false)}
        >
          <div
            className="bg-background rounded-lg p-6 w-full max-w-md space-y-4 shadow-lg"
            onClick={(clickEvent) => clickEvent.stopPropagation()}
          >
            <h2 className="text-lg font-semibold">Place Test AI Call</h2>
            <p className="text-sm text-muted-foreground">
              Dials the number you provide and connects this agent. Bypasses workflows for fast iteration.
            </p>
            <div className="space-y-1">
              <label className="text-sm font-medium">Call to</label>
              <Input
                type="tel"
                placeholder="+1 555 555 1234"
                value={testCallNumber}
                onChange={(changeEvent) => setTestCallNumber(changeEvent.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Call from</label>
              <Select
                value={testCallFromId || "__auto__"}
                onValueChange={(value) => setTestCallFromId(value === "__auto__" ? "" : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Auto (agent's number)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">Auto (agent's number)</SelectItem>
                  {(allNumbers ?? [])
                    .filter((num) => num.active)
                    .map((num) => (
                      <SelectItem key={num.id} value={num.id}>
                        {num.alias ? `${num.alias} (${num.number})` : num.number}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTestCallDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => handlePlaceTestCall()} disabled={isPlacingTestCall}>
                {isPlacingTestCall && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Place Call
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={consentPromptOpen} onOpenChange={setConsentPromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No call consent on file</AlertDialogTitle>
            <AlertDialogDescription>
              {testCallNumber} has no recorded call consent, so the call was blocked. If you have
              the owner's permission to call, record consent now and place the call. The number
              must belong to an existing contact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={isGrantingConsent} onClick={handleRecordConsentAndCall}>
              Record consent and call
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
